import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'

import { JEPX_AREA_CODES } from '~/models/area'
import { now as serverNow } from '~/models/clock'
import {
  formatDateValue,
  isSameDay,
  parseIsoDate,
  startOfTodayJst,
} from '~/models/dateTime'

/**
 * 時間前入札 3 画面 (入札管理・板発注・約定履歴) 共通の売買区分フィルタ値。
 * `'all'` は絞り込みなし。
 */
export type IntradaySideFilter = 'all' | 'buy' | 'sell'

const VALID_SIDE_VALUES: readonly IntradaySideFilter[] = ['all', 'buy', 'sell']

/**
 * 時間前入札 3 画面 (入札管理〈入札追加 / 入札履歴〉・板発注・約定履歴) の
 * 全体フィルタ state (in-memory 形状)。 spec `bidding/intraday-bidding.md`
 * **AC-BIN-102 [P0]** が「単一の全体フィルター（受渡日 / エリア / 売買区分）を
 * 共有し、いずれかの画面でフィルタを変更した場合、他の画面へ遷移した時点で
 * 同一の選択状態を引き継ぐ」と規定する。
 *
 * 受渡日の選択可能範囲は画面ごとに異なる (AC-BIN-000a: 入札追加・板発注は
 * 当日以降、 AC-BIN-024/約定履歴 §2 ビジネスルール: 入札履歴・約定履歴は
 * 過去日も選択可能) が、 **値そのものは単一** (= 本 state 1 つ)。 範囲の
 * clamp + 過去日リセット (AC-BIN-102 本文「過去日を選択した状態で入札追加・
 * 板発注へ遷移した場合は、受渡日を当日にリセットし、その旨をトーストで通知
 * する」) は呼び出し側 (`IntradayBidding/index.tsx`) が画面遷移を検知して行う。
 *
 * 入札一覧固有のフィルタ (ステータス / コマ / ワークフロー、 AC-BIN-018 の
 * 画面内フィルタ) は他のタブと共有しないので、 ここには含めない。 保持は
 * AC-SC-031 に従い、 別の key で `bidListFilters.ts` が持つ (#12636)。
 * 売買区分 (`side`) は AC-BIN-018 が「全体フィルターの値を
 * そのまま適用する」と定めるため、 入札追加タブの入札一覧フィルタも本 state の
 * `side` をそのまま消費する (ローカル state は持たない)。
 */
export type IntradayGlobalFilters = {
  targetDate: IntradayTargetDateFilter
  selectedAreas: Set<AreaCode>
  side: IntradaySideFilter
}

/**
 * 全体フィルタの受渡日。 `'today'` は「当日」 を選んでいる状態で、 日付が変わると
 * その日の当日を指す。 当日以外を選んだ時だけ日付 (カレンダー日 `Date`) で持つ。
 *
 * 当日を日付で持つと、 翌日以降の再訪や画面を開いたまま 0 時を跨いだだけで過去日に
 * なり、 過去日を選んでいない利用者にも過去日リセットのトースト (AC-BIN-102) が出る。
 */
export type IntradayTargetDateFilter = 'today' | Date

/** 利用者が選んだ受渡日を全体フィルタの値にする (当日なら `'today'`)。 */
export function toIntradayTargetDateFilter(
  date: Date,
  now: Date = serverNow(),
): IntradayTargetDateFilter {
  return isSameDay(date, startOfTodayJst(now)) ? 'today' : date
}

/**
 * 全体フィルタの受渡日をカレンダー日にする。 `today` は呼び出し側が日付境界でだけ
 * 作り直した当日を渡す (分 tick ごとに新しい参照を作らないため)。
 */
export function resolveIntradayTargetDate(
  value: IntradayTargetDateFilter,
  today: Date,
): Date {
  return value === 'today' ? today : value
}

/**
 * フィルタ既定値。 受渡日は「当日」、 エリアは「すべて」 (= 全 9 エリア、
 * `IntradayAreaMultiSelect` の「空選択 = すべて」 規約と揃える)、 売買区分は「すべて」。
 */
export function defaultIntradayGlobalFilters(): IntradayGlobalFilters {
  return {
    targetDate: 'today',
    selectedAreas: new Set(JEPX_AREA_CODES),
    side: 'all',
  }
}

/** localStorage に載せる直列化形状。 受渡日は `'today'` か `YYYY-MM-DD`。 */
type PersistedShape = {
  targetDate: string
  areaCodes: number[]
  side: IntradaySideFilter
}

/** フィルタ state を localStorage 用 JSON 文字列へ直列化する。 */
export function serializeIntradayGlobalFilters(
  filters: IntradayGlobalFilters,
): string {
  const shape: PersistedShape = {
    targetDate:
      filters.targetDate === 'today'
        ? 'today'
        : formatDateValue(filters.targetDate),
    areaCodes: [...filters.selectedAreas].sort((a, b) => a - b),
    side: filters.side,
  }
  return JSON.stringify(shape)
}

/**
 * localStorage の生文字列をフィルタ state へ復元する。 壊れた値・stale enum・
 * 未知フィールドは黙って既定へフォールバックする (`ProcurementPlanListFilters`
 * と同方針、 deploy skew / 手書き改変を crash させない)。
 *
 * 受渡日は AC-SC-031「再訪時に前回の選択状態を復元する」 に従い**無条件に復元**
 * する (過去日も正規の状態、 入札履歴・約定履歴は過去日を扱うため)。 `'today'` は
 * 復元した日の当日になる。 画面ごとの選択可能範囲の clamp は呼び出し側の責務
 * (このモジュールの JSDoc 参照)。
 */
export function deserializeIntradayGlobalFilters(
  raw: string | undefined,
): IntradayGlobalFilters {
  const defaults = defaultIntradayGlobalFilters()
  if (raw == null) return defaults

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return defaults
  }
  if (typeof parsed !== 'object' || parsed === null) return defaults
  const p = parsed as Record<string, unknown>

  // `'today'` は既定値と同じなので、 日付の形の文字列だけを読む。
  let targetDate = defaults.targetDate
  if (typeof p.targetDate === 'string') {
    const restored = parseIsoDate(p.targetDate)
    // 往復して元文字列に一致することを確認し、不正な暦日 ("2026-02-31" 等の
    // 繰り上げ) は既定へ戻す (`ProcurementPlanListFilters` と同ガード)。
    if (restored !== undefined && formatDateValue(restored) === p.targetDate) {
      targetDate = restored
    }
  }

  const validAreaCodes = new Set<number>(JEPX_AREA_CODES)
  const restoredAreaCodes = Array.isArray(p.areaCodes)
    ? p.areaCodes.filter(
        (v): v is number => typeof v === 'number' && validAreaCodes.has(v),
      )
    : []
  // 空集合 (= 復元に失敗した / 全解除して保存された) は「すべて」 として扱う
  // (`IntradayAreaMultiSelect` の「部品からは空集合を出さない」 契約と揃える)。
  const selectedAreas =
    restoredAreaCodes.length > 0
      ? new Set(restoredAreaCodes as AreaCode[])
      : defaults.selectedAreas

  const side = VALID_SIDE_VALUES.includes(p.side as IntradaySideFilter)
    ? (p.side as IntradaySideFilter)
    : defaults.side

  return { targetDate, selectedAreas, side }
}
